import React, { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, X, CreditCard, Users, LifeBuoy, Star, Mail, UserPlus, Clock, AlertTriangle, ChevronRight, MonitorSmartphone, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNotificationStore } from '../../store/notificationStore';
import { useAuth } from '../../hooks/useAuth';
import { Notification, NotificationCategory, CATEGORY_COLORS } from '../../types/notifications';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import {
  getPermission,
  requestPermission,
  showDesktopNotification,
  isSupported as desktopNotifSupported,
  hasAskedBefore,
  markAsked,
  type DesktopPermission,
} from '../../lib/desktopNotifications';
import { subscribe as subscribeToPush, isPushSupported } from '../../lib/pushSubscription';
import { logWarn } from '../../lib/logger';

// Mapping des icônes par catégorie
const getCategoryIcon = (category?: NotificationCategory) => {
  switch (category) {
    case 'stripe':
      return CreditCard;
    case 'crm':
      return Users;
    case 'support':
      return LifeBuoy;
    case 'feature':
      return Star;
    case 'newsletter':
      return Mail;
    case 'onboarding':
      return UserPlus;
    case 'inbox':
      return MessageCircle;
    default:
      return Bell;
  }
};

// Couleurs par priorité
const getPriorityClass = (priority?: string) => {
  switch (priority) {
    case 'urgent':
      return 'border-l-4 border-l-red-500 bg-red-50';
    case 'high':
      return 'border-l-4 border-l-orange-400';
    case 'normal':
      return 'border-l-4 border-l-blue-300';
    default:
      return 'border-l-4 border-l-neutral-200';
  }
};

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onClose: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead, onClose }) => {
  // Escalation gets the alert look (red + triangle) whatever its category.
  const isEscalation = notification.type === 'CLIENT_CONVERSATION_ESCALATED';
  const Icon = isEscalation ? AlertTriangle : getCategoryIcon(notification.category);
  const categoryColor = isEscalation
    ? 'bg-red-100 text-red-600'
    : notification.category
      ? CATEGORY_COLORS[notification.category]
      : 'bg-desk-subtle text-desk-soft';
  
  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    if (notification.link) {
      onClose();
    }
  };

  const content = (
    <div
      className={`
        p-3 hover:bg-desk-subtle transition-colors cursor-pointer
        ${!notification.read ? 'bg-blue-50/50' : ''}
        ${getPriorityClass(notification.priority)}
      `}
      onClick={handleClick}
    >
      <div className="flex gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${categoryColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm font-medium truncate ${!notification.read ? 'text-neutral-900' : 'text-neutral-700'}`}>
              {notification.title}
            </p>
            {!notification.read && (
              <span className="w-2 h-2 bg-desk-accent rounded-full flex-shrink-0 mt-1.5" />
            )}
          </div>
          <p className="text-xs text-desk-muted line-clamp-2 mt-0.5">
            {notification.content}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Clock className="w-3 h-3 text-desk-muted" />
            <span className="text-xs text-desk-muted">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: fr })}
            </span>
            {notification.priority === 'urgent' && (
              <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                <AlertTriangle className="w-3 h-3" />
                Urgent
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (notification.link) {
    return <Link to={notification.link}>{content}</Link>;
  }

  return content;
};

export const NotificationBell: React.FC<{
  /** The button's look where the header's does not fit — Encre's sidebar. */
  buttonClassName?: string;
}> = ({ buttonClassName }) => {
  const { user } = useAuth();
  const { notifications, unreadCount, fetchNotifications, markAsRead, markAllAsRead, addToast } = useNotificationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 64, right: 16 });
  const [desktopPerm, setDesktopPerm] = useState<DesktopPermission>(() => getPermission());
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => hasAskedBefore());
  /** Feedback inline du flux « Activer » — chaque échec était silencieux (bouton « mort »). */
  const [pushFeedback, setPushFeedback] = useState<string | null>(null);
  const [enabling, setEnabling] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleEnableDesktop = async () => {
    setPushFeedback(null);
    setEnabling(true);
    try {
      const result = await requestPermission();
      setDesktopPerm(result);
      if (result !== 'granted') {
        // denied → la bannière ambre « bloquées » prend le relais ; default →
        // le prompt navigateur a été ignoré/auto-fermé, on explique quoi faire.
        if (result === 'default') {
          setPushFeedback('La demande a été ignorée par le navigateur. Cliquez à nouveau sur « Activer » puis choisissez « Autoriser ».');
        }
        return;
      }
      // 1) Notif de confirmation immediate (canal in-session)
      showDesktopNotification({
        title: 'Notifications activées',
        body: 'Vous recevrez les alertes AutoQuote sur votre bureau.',
        tag: 'aq-desktop-enabled',
      });
      // 2) Inscription Web Push (push réel même app fermée). En cas d'échec, la
      // bannière reste affichée avec l'erreur pour permettre un nouvel essai.
      if (isPushSupported()) {
        try {
          await subscribeToPush();
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          logWarn('push', 'subscription failed (in-session notifs still active)', { error: message });
          setPushFeedback(`Notifications bureau actives, mais l'inscription au push a échoué : ${message}`);
          return;
        }
      }
      setBannerDismissed(true);
    } finally {
      setEnabling(false);
    }
  };

  const handleDismissBanner = () => {
    markAsked();
    setBannerDismissed(true);
  };

  // Charger les notifications au montage
  useEffect(() => {
    if (user?.id) {
      fetchNotifications(user.id);
    }
  }, [user?.id, fetchNotifications]);

  // Écouter les nouvelles notifications en temps réel
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          // Recharger les notifications
          await fetchNotifications(user.id);
          
          // Afficher un toast pour la nouvelle notification
          if (payload.new) {
            const newNotification = payload.new as Notification;
            // Attendre un peu pour que la notification soit dans le store
            setTimeout(() => {
              const currentNotifications = useNotificationStore.getState().notifications;
              const notification = currentNotifications.find(n => n.id === newNotification.id);
              if (!notification || notification.read) return;
              // Niveau « Fil seul » : la notification rejoint la cloche mais ne
              // doit interrompre par aucun canal (toast ni bureau). Le web push
              // est coupé symétriquement côté send-push.
              if (!notification.push_eligible) return;
              addToast(notification);
              // Notif desktop si la permission est accordee
              showDesktopNotification({
                title: notification.title,
                body: notification.content,
                tag: `aq-notif-${notification.id}`,
                link: notification.link || null,
              });
            }, 100);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchNotifications]);

  // Calculer la position du dropdown pour qu'il reste dans l'écran
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        if (!buttonRef.current) return;
        
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 384; // w-96 = 384px
        const spacing = 8; // mt-2 = 8px
        const margin = 16; // marge minimale depuis les bords
        
        // Position par défaut : aligner le bord droit du dropdown avec le bord droit du bouton
        let rightPosition = window.innerWidth - buttonRect.right;
        
        // Vérifier si le dropdown dépasse à gauche de l'écran
        const leftPosition = buttonRect.right - dropdownWidth;
        if (leftPosition < margin) {
          // Le dropdown dépasse à gauche, l'aligner sur le bord gauche avec une marge
          rightPosition = window.innerWidth - margin - dropdownWidth;
        }
        
        // Vérifier si le dropdown dépasse à droite (ne devrait pas arriver avec right-0, mais sécurité)
        if (rightPosition < margin) {
          rightPosition = margin;
        }
        
        setDropdownPosition({
          top: buttonRect.bottom + spacing,
          right: rightPosition,
        });
      };
      
      updatePosition();
      
      // Réajuster lors du scroll ou du redimensionnement
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  // Fermer le dropdown en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
  };

  const handleMarkAllAsRead = async () => {
    if (user?.id) {
      await markAllAsRead(user.id);
    }
  };

  const recentNotifications = notifications.slice(0, 10);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton cloche */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName ?? 'relative p-2 text-desk-muted hover:text-desk-soft hover:bg-desk-subtle rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-desk-ring focus:ring-offset-2'}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-semibold text-white bg-red-500 rounded-full px-1 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="fixed w-96 max-w-[calc(100vw-2rem)] bg-desk-card rounded-xl shadow-pop border border-desk-line overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-desk-line">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-desk-soft" />
                <h3 className="font-medium text-desk-ink">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-desk-subtle text-desk-ink rounded-full">
                    {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="p-1.5 text-desk-muted hover:text-desk-soft hover:bg-desk-subtle rounded-lg transition-colors"
                    title="Tout marquer comme lu"
                  >
                    <CheckCheck className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 text-desk-muted hover:text-desk-soft hover:bg-desk-subtle rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Banniere d'activation des notifications bureau */}
          {desktopNotifSupported() && desktopPerm === 'default' && !bannerDismissed && (
            <div className="px-4 py-3 bg-desk-subtle border-b border-desk-ring flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-desk-accent flex items-center justify-center flex-shrink-0">
                <MonitorSmartphone className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-desk-ink">Notifications bureau</p>
                <p className="text-xs text-desk-soft mt-0.5">
                  Recevez les alertes AutoQuote directement sur votre bureau, meme onglet ferme.
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={handleEnableDesktop}
                    disabled={enabling}
                    className="text-xs font-medium px-3 py-1.5 bg-desk-accent text-white rounded-md hover:brightness-[0.94] transition disabled:opacity-60"
                  >
                    {enabling ? 'Activation…' : 'Activer'}
                  </button>
                  <button
                    onClick={handleDismissBanner}
                    className="text-xs font-medium px-3 py-1.5 text-desk-soft hover:text-desk-ink transition-colors"
                  >
                    Plus tard
                  </button>
                </div>
                {pushFeedback && (
                  <p className="mt-2 text-xs text-desk-signal">{pushFeedback}</p>
                )}
              </div>
            </div>
          )}
          {desktopNotifSupported() && desktopPerm === 'denied' && !bannerDismissed && (
            <div className="px-4 py-2.5 bg-desk-subtle border-b border-desk-line flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-desk-signal flex-shrink-0" />
              <p className="text-xs text-desk-ink flex-1">
                Notifications bureau bloquees. Autorisez-les dans les parametres du navigateur.
              </p>
              <button
                onClick={handleDismissBanner}
                className="text-xs text-desk-signal hover:text-desk-ink"
              >
                OK
              </button>
            </div>
          )}

          {/* Liste des notifications */}
          <div className="max-h-[400px] overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-desk-subtle rounded-full flex items-center justify-center">
                  <Bell className="w-8 h-8 text-desk-muted" />
                </div>
                <p className="text-desk-muted font-medium">Aucune notification</p>
                <p className="text-sm text-desk-muted mt-1">Vous êtes à jour !</p>
              </div>
            ) : (
              <div className="divide-y divide-desk-line">
                {recentNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onClose={() => setIsOpen(false)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 bg-desk-subtle border-t border-desk-line">
              <Link
                to="/admin/settings/notification-center"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 text-sm text-desk-ink hover:text-desk-ink font-medium transition-colors"
              >
                Voir toutes les notifications
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
